import { useEffect, useCallback, useLayoutEffect, useRef, useState } from 'react'
import { Portal, Box, useColorModeValue } from '@chakra-ui/react'
import { Link as RouterLink } from 'react-router-dom'
import type { RowActionItem } from './types'
import { renderRowContextMenuItems } from './rowActions'
import { useThemeColors } from '@/hooks/useThemeColors'

export interface RowContextMenuState {
  isOpen: boolean
  x: number
  y: number
  items: RowActionItem[]
}

export const CLOSED_ROW_CONTEXT_MENU: RowContextMenuState = {
  isOpen: false,
  x: 0,
  y: 0,
  items: [],
}

interface RowContextMenuProps {
  menuState: RowContextMenuState
  onClose: () => void
  scrollContainerRef?: React.RefObject<HTMLElement | null>
}

const INTERACTIVE_SELECTOR = 'button, a, input, textarea, select, [role="checkbox"], [role="button"]'
const VIEWPORT_PADDING = 8

export function isInteractiveContextMenuTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false
  return !!target.closest(INTERACTIVE_SELECTOR)
}

function clampPosition(x: number, y: number, width: number, height: number) {
  const maxX = window.innerWidth - width - VIEWPORT_PADDING
  const maxY = window.innerHeight - height - VIEWPORT_PADDING
  return {
    x: Math.max(VIEWPORT_PADDING, Math.min(x, maxX)),
    y: Math.max(VIEWPORT_PADDING, Math.min(y, maxY)),
  }
}

export function RowContextMenu({ menuState, onClose, scrollContainerRef }: RowContextMenuProps) {
  const colors = useThemeColors()
  const menuBg = colors.cardBg
  const borderColor = colors.border
  const hoverBg = useColorModeValue('gray.100', 'whiteAlpha.100')
  const menuRef = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState({ x: menuState.x, y: menuState.y })

  const handleClose = useCallback(() => {
    onClose()
  }, [onClose])

  useLayoutEffect(() => {
    if (!menuState.isOpen) return

    const menuEl = menuRef.current
    if (!menuEl) {
      setPosition({ x: menuState.x, y: menuState.y })
      return
    }

    const { width, height } = menuEl.getBoundingClientRect()
    setPosition(clampPosition(menuState.x, menuState.y, width, height))
  }, [menuState.isOpen, menuState.x, menuState.y, menuState.items])

  useEffect(() => {
    if (!menuState.isOpen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose()
    }

    const handlePointerDown = (e: MouseEvent) => {
      const target = e.target as Element
      if (target.closest('[data-row-context-menu]')) return
      handleClose()
    }

    const scrollEl = scrollContainerRef?.current
    const handleScroll = () => handleClose()

    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('mousedown', handlePointerDown)
    scrollEl?.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('mousedown', handlePointerDown)
      scrollEl?.removeEventListener('scroll', handleScroll)
    }
  }, [menuState.isOpen, handleClose, scrollContainerRef])

  if (!menuState.isOpen || menuState.items.length === 0) return null

  return (
    <Portal>
      <Box
        ref={menuRef}
        data-row-context-menu
        role="menu"
        position="fixed"
        top={`${position.y}px`}
        left={`${position.x}px`}
        zIndex={2000}
        minW="180px"
        bg={menuBg}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="md"
        boxShadow="lg"
        py={1}
        onClick={(e) => e.stopPropagation()}
        onContextMenu={(e) => e.preventDefault()}
      >
        {renderRowContextMenuItems(menuState.items, RouterLink, handleClose, hoverBg)}
      </Box>
    </Portal>
  )
}
